import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { queryKeys } from '@/api/queryKeys'
import type { components } from '@/../types/api'

export type NotificationCatalog = components['schemas']['CatalogOut']
export type EventCatalogOut = components['schemas']['EventCatalogOut']
export type VarSpecOut = components['schemas']['VarSpecOut']
export type SettingsListOut = components['schemas']['SettingsListOut']
export type EventSettingOut = components['schemas']['EventSettingOut']
export type SettingUpdateIn = components['schemas']['SettingUpdateIn']
export type PreviewIn = components['schemas']['PreviewIn']
export type PreviewOut = components['schemas']['PreviewOut']

/** Catálogo estático de eventos (metadados, variáveis, templates default). */
export function useNotificationCatalog() {
  return useQuery({
    queryKey: queryKeys.notifications.catalog(),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/notifications/catalog')
      if (!data) throw new Error('catalog empty')
      return data.data
    },
    staleTime: 5 * 60_000, // catálogo raramente muda
  })
}

/** Overrides salvos + valores efetivos de um escopo (null = global). */
export function useNotificationSettings(channelSlug: string | null) {
  return useQuery({
    queryKey: queryKeys.notifications.settings(channelSlug),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/notifications/settings', {
        params: { query: channelSlug ? { channel_slug: channelSlug } : {} },
      })
      if (!data) throw new Error('settings empty')
      return data.data
    },
    staleTime: 30_000,
  })
}

/** Upsert parcial de um override (enabled/templates/mention). */
export function useUpdateNotificationSetting() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: SettingUpdateIn) => {
      const { data } = await api.PUT('/api/v1/notifications/settings', { body: input })
      if (!data) throw new Error('update empty')
      return data.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.notifications.all })
    },
  })
}

/** Remove um override (volta a herdar). */
export function useDeleteNotificationSetting() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { event_key: string; channel_slug?: string | null }) => {
      const { data } = await api.DELETE('/api/v1/notifications/settings', { body: input })
      if (!data) throw new Error('delete empty')
      return data.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.notifications.all })
    },
  })
}

/** Renderiza um template com dados de exemplo (sem enviar). */
export function useNotificationPreview() {
  return useMutation({
    mutationFn: async (input: PreviewIn) => {
      const { data } = await api.POST('/api/v1/notifications/preview', { body: input })
      if (!data) throw new Error('preview empty')
      return data.data
    },
  })
}

/** Dispara uma notificação de teste real pro webhook do escopo. */
export function useTestNotification() {
  return useMutation({
    mutationFn: async (input: { event_key: string; channel_slug?: string | null }) => {
      const { data } = await api.POST('/api/v1/notifications/test', { body: input })
      if (!data) throw new Error('test empty')
      return data.data
    },
  })
}
